// src/api/axios.js

import axios from "axios";
import { BACK_END_API_URL } from "../utils/LS-CONSTANT";
import { logoutUser } from "../utils/utils";
import { decodeJWT, getUserIdFromToken, getUsernameFromToken, isTokenExpired } from "../utils/jwtUtils";
import logger from "../utils/logger";

export function getAccessToken() {
  const keys = Object.keys(localStorage);

  const getCookie = (name) => {
    const match = document.cookie.match(
        new RegExp("(^| )" + name + "=([^;]+)")
    );
    return match ? decodeURIComponent(match[2]) : null; // Decode URI component
  };

  // Priority 1: Check cookie for access_token
  const cookieToken = getCookie("access_token");
  if (cookieToken) {
    // Validate token is not expired
    if (!isTokenExpired(cookieToken)) {
      return cookieToken;
    } else {
      logger.warn('Access token from cookie is expired');
      // Optionally: Trigger token refresh here
    }
  }

  // Priority 2: Check localStorage for Cognito token
  const cognitoKey = keys.find(
      (k) =>
          k.startsWith("CognitoIdentityServiceProvider.") &&
          k.endsWith(".accessToken")
  );

  if (cognitoKey) {
    const token = localStorage.getItem(cognitoKey);
    if (token && !isTokenExpired(token)) {
      return token;
    }
  }

  return null;
}

// Mirrors getAccessToken() but reads id_token instead. The id_token carries
// user profile claims (email, name, etc.) that the access_token does not.
export function getIdToken() {
  const keys = Object.keys(localStorage);

  const getCookie = (name) => {
    const match = document.cookie.match(
        new RegExp("(^| )" + name + "=([^;]+)")
    );
    return match ? decodeURIComponent(match[2]) : null;
  };

  // Priority 1: Check cookie for id_token
  const cookieToken = getCookie("id_token");
  if (cookieToken) {
    if (!isTokenExpired(cookieToken)) {
      return cookieToken;
    } else {
      logger.warn('ID token from cookie is expired');
    }
  }

  // Priority 2: Check localStorage for Cognito id token
  const cognitoKey = keys.find(
      (k) =>
          k.startsWith("CognitoIdentityServiceProvider.") &&
          k.endsWith(".idToken")
  );

  if (cognitoKey) {
    const token = localStorage.getItem(cognitoKey);
    if (token && !isTokenExpired(token)) {
      return token;
    } else if (token) {
      logger.warn('ID token from localStorage is expired');
    }
  }

  return null;
}

// Replace with your backend URL
const LS_API = axios.create({
  baseURL: BACK_END_API_URL
});

LS_API.interceptors.request.use((config) => {
  const token = getAccessToken();

  if (token) {
    // Add Authorization header
    config.headers.Authorization = `Bearer ${token}`;

    // Extract and add x-user-id from token
    const username = getUsernameFromToken(token);
    if (username) {
      config.headers['x-user-id'] = username;
    } else {
      // Fallback to subject/user_id
      const userId = getUserIdFromToken(token);
      if (userId) {
        config.headers['x-user-id'] = userId;
      }
    }

    // Send id_token separately so backend can read email/profile claims.
    // Backend validates this against a dedicated id-token JwtDecoder
    // (different audience check) - see IdTokenFilter on the Spring side.
    const idToken = getIdToken();
    if (idToken) {
      config.headers['x-id-token'] = idToken;
    } else {
      logger.warn('No id_token available; backend will not receive lsUserEmailAddress');
    }

    // Optional: Add token claims for debugging
    if (process.env.NODE_ENV === 'development') {
      const decoded = decodeJWT(token);
      if (decoded) {
        config.headers['x-token-claims'] = JSON.stringify({
          sub: decoded.sub,
          username: decoded.username || decoded['cognito:username'],
          email: decoded.email,
          exp: new Date(decoded.exp * 1000).toISOString()
        });
      }
    }
  }

  return config;
});

LS_API.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (error?.response?.status === 401) {
        // Check if token is expired
        const token = getAccessToken();
        if (token && isTokenExpired(token)) {
          logger.log('Token expired, logging out');
        }
        logoutUser();
      }
      return Promise.reject(error);
    }
);

export default LS_API;